import { useEffect, useState, useContext } from 'react';
import Layout from '@/components/Layout';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, ArrowLeft, Trash2 } from 'lucide-react';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_LABELS = {
  'basic': 'BASIC PRACTICE',
  'comps_ready': "COMP'S READY",
  'advanced': 'ADVANCED'
};

export default function FlashcardsCategoryPage() {
  const { tier, categorySlug } = useParams();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [flashcards, setFlashcards] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [tier, categorySlug]);

  const loadData = async () => {
    try {
      // Load all flashcards and filter by tier and category
      const [flashcardsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/flashcards`),
        axios.get(`${API}/test-categories`) // Public endpoint
      ]);
      
      // Find the category
      const foundCategory = categoriesRes.data.find(cat => cat.slug === categorySlug);
      setCategory(foundCategory);
      
      // Filter flashcards for this tier and category
      const filtered = flashcardsRes.data.filter(
        f => f.tier === tier && f.category === categorySlug
      );
      setFlashcards(filtered);
    } catch (error) {
      console.error('Failed to load flashcards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFlashcardClick = (flashcardId) => {
    navigate(`/flashcards/${flashcardId}`);
  };

  const handleBackClick = () => {
    navigate('/flashcards-center');
  };

  const handleDelete = async (flashcardId, title) => {
    if (!window.confirm(`Delete Flashcard set "${title || 'this set'}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/flashcards/${flashcardId}`, { withCredentials: true });
      setFlashcards(prev => prev.filter(f => f.id !== flashcardId));
      toast.success('Flashcard set deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete flashcard set');
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <div 
        className="relative bg-gradient-to-br from-amber-400 via-orange-400 to-amber-500 h-[300px] mb-12 flex items-center justify-center"
      >
        <div className="container mx-auto px-6">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 bg-[#2C1810]/90 text-[#F4E4C1] font-bold px-4 py-2 rounded-lg hover:bg-[#2C1810] transition-all mb-6"
          >
            <ArrowLeft className="w-6 h-6" />
            <span className="text-lg">Back to Flashcards Center</span>
          </button>
          <div className="text-center">
            <div className="text-6xl mb-4">{category?.icon || '📚'}</div>
            <h1 className="text-5xl md:text-6xl retro-title text-white mb-2">
              {category?.name.toUpperCase() || categorySlug.toUpperCase()}
            </h1>
            <p className="text-xl text-white font-bold mb-2">
              {TIER_LABELS[tier] || tier.toUpperCase()}
            </p>
            <p className="text-lg text-white opacity-90">
              {flashcards.length} Flashcard Set{flashcards.length !== 1 ? 's' : ''} Available
            </p>
          </div>
        </div>
      </div>

      {/* Flashcard Sets Grid */}
      <div className="container mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="text-2xl text-[#6B4423]">Loading flashcards...</div>
          </div>
        ) : flashcards.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {flashcards.map((flashcard) => (
              <div key={flashcard.id} className="relative" data-testid={`flashcards-card-${flashcard.id}`}>
                {isAdmin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(flashcard.id, flashcard.test_title); }}
                    className="absolute top-3 right-3 z-10 bg-[#E85D4D] text-white p-2 rounded-full hover:bg-[#d14a3b] transition-colors shadow-lg"
                    title="Delete this flashcard set"
                    data-testid={`delete-flashcards-button-${flashcard.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleFlashcardClick(flashcard.id)}
                  className="w-full cassette-card bg-[#F4E4C1] p-8 text-left transition-all transform hover:scale-105 hover:shadow-2xl"
                >
                  <div className="flex items-start justify-between mb-4">
                    <BookOpen className="w-12 h-12 text-amber-600" />
                    <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {flashcard.cards?.length || 0} Cards
                    </span>
                  </div>
                  <h3 className="text-2xl retro-title text-[#2C1810] mb-3">
                    {flashcard.test_title || 'Flashcard Set'}
                  </h3>
                  <p className="text-[#6B4423] text-sm">
                    Study materials from test
                  </p>
                  <div className="mt-4 pt-4 border-t-2 border-[#6B4423]/20">
                    <p className="text-xs text-[#6B4423] opacity-70">
                      Created: {new Date(flashcard.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <BookOpen className="w-24 h-24 text-[#6B4423] mx-auto mb-6 opacity-50" />
            <h3 className="text-3xl retro-title text-[#2C1810] mb-4">
              No Flashcards Available
            </h3>
            <p className="text-lg text-[#6B4423]">
              No flashcard sets have been created for this category yet.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
